import { FLOOR_PLAN_MESSAGES, rectsOverlap } from "./floor-plan.js";
/** The station whose tile covers the square `(x, y)`, if any. */
export function stationAt(stations, x, y) {
    return stations.find((station) => x >= station.x && x < station.x + station.w && y >= station.y && y < station.y + station.h);
}
/**
 * `stationId` dropped with its top-left on `(x, y)`. A station already there is swapped onto the dropped one's old
 * square, or sent to the tray when the dropped one came from the tray. Dropping a station on itself changes nothing.
 */
export function dropStation(stations, stationId, x, y) {
    const moving = stations.find((station) => station.stationId === stationId);
    const occupant = stationAt(stations, x, y);
    if (occupant !== undefined && occupant.stationId === stationId)
        return [...stations];
    const moved = { stationId, x, y, w: moving?.w ?? 1, h: moving?.h ?? 1 };
    const out = [];
    for (const station of stations) {
        if (station.stationId === stationId)
            continue;
        if (occupant !== undefined && station.stationId === occupant.stationId) {
            // Swapped onto the old square, or back to the tray (left out) when the dropped one had none.
            if (moving !== undefined)
                out.push({ ...occupant, x: moving.x, y: moving.y });
            continue;
        }
        out.push(station);
    }
    out.push(moved);
    return out;
}
/** Take a station off the floor, back to the tray. */
export function trayStation(stations, stationId) {
    return stations.filter((station) => station.stationId !== stationId);
}
/** The arrow keys: one square in a direction, kept inside the grid, swapping like a drop. */
export function nudgeStation(stations, stationId, dx, dy, columns, rows) {
    const station = stations.find((candidate) => candidate.stationId === stationId);
    if (station === undefined)
        return [...stations];
    const x = Math.min(Math.max(station.x + dx, 0), Math.max(columns - station.w, 0));
    const y = Math.min(Math.max(station.y + dy, 0), Math.max(rows - station.h, 0));
    if (x === station.x && y === station.y)
        return [...stations];
    return dropStation(stations, stationId, x, y);
}
/**
 * Auto-layout in number order: `ordered` are the station ids to place, lowest number first (placed ones included —
 * the layout starts again from the top-left). Each gets a 1 × 1 square, row by row, skipping squares a fixture in
 * `blocked` covers. Stations that do not fit are left out (they stay in the tray).
 */
export function autoLayoutStations(ordered, columns, rows, blocked) {
    const out = [];
    let next = 0;
    for (let y = 0; y < rows && next < ordered.length; y += 1) {
        for (let x = 0; x < columns && next < ordered.length; x += 1) {
            const square = { x, y, w: 1, h: 1 };
            if (blocked.some((rect) => rectsOverlap(rect, square)))
                continue;
            out.push({ stationId: ordered[next], ...square });
            next += 1;
        }
    }
    return out;
}
/**
 * What a floor-size stepper says before it changes the grid: undefined when every placed zone, fixture and station
 * still fits in `columns` × `rows`, else the rule's own message — the stepper refuses rather than cutting through.
 */
export function resizeRefusal(plan, columns, rows) {
    const items = [...plan.zones, ...plan.fixtures, ...(plan.stations ?? [])];
    return items.some((item) => item.x + item.w > columns || item.y + item.h > rows) ? FLOOR_PLAN_MESSAGES.outsideGrid : undefined;
}
//# sourceMappingURL=arrange.js.map